import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  useTheme,
  IconButton,
  Backdrop,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
  Avatar,
  Paper,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Edit,
  Add,
  Save,
  MonetizationOn,
  Delete,
  LocationOn,
  Directions,
  ArrowBack,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  createLongDistance,
  deleteLongDistance,
  getLongDistance,
  updateLongDistance,
  getLocations,
  createLocation,
  updateLocation,
  deleteLocation,
} from "../../redux/action/rate";
import usaStates from "../../assets/utils/statesOfUSA";

interface Location {
  id: number;
  location_name: string;
}

interface DistanceRate {
  id?: number;
  rate_type: string;
  rate: string | number;
  location_id?: number;
  location?: Location;
}

const LongDistance: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [distanceRates, setDistanceRates] = useState<DistanceRate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [newRangeStart, setNewRangeStart] = useState("");
  const [newRangeEnd, setNewRangeEnd] = useState("");
  const [rate, setRate] = useState("");
  const [newLocationName, setNewLocationName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editedRangeStart, setEditedRangeStart] = useState("");
  const [editedRangeEnd, setEditedRangeEnd] = useState("");
  const [editedRate, setEditedRate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null
  );
  let states = usaStates;
  const carrierId = parseInt(localStorage.getItem("CarrierId") || "0", 10);

  useEffect(() => {
    if (carrierId && !isNaN(carrierId)) {
      loadData();
    }
  }, [dispatch, carrierId]);

  const loadData = async () => {
    setIsLoading(true);
    setLoadingMessage("Loading data...");

    try {
      const [locationsRes, ratesRes] = await Promise.all([
        dispatch<any>(getLocations(carrierId)),
        dispatch<any>(getLongDistance(carrierId)),
      ]);

      const locationsResult = unwrapResult(locationsRes);
      const ratesResult = unwrapResult(ratesRes);
      setLocations(locationsResult);
      setDistanceRates(ratesResult || []);
    } catch (err) {
      setLoadingMessage("Failed to load data.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const validateRange = (start: string, end: string, editingId: number | null = null) => {
    const from = parseFloat(start);
    const to = parseFloat(end);
    if (isNaN(from) || isNaN(to)) {
      return "Please enter valid numbers";
    }
    if (from >= to) {
      return "The 'From' value must be less than the 'To' value";
    }
    const overlapping = distanceRates.some(rate => {
      if (rate.location?.id !== selectedLocation?.id) return false;
      if (rate.id === editingId) return false; 
      
      const [existingFrom, existingTo] = rate.rate_type.split(' to ').map(Number);
      
      return (
        (from >= existingFrom && from < existingTo) ||    
        (to > existingFrom && to <= existingTo) ||        
        (from <= existingFrom && to >= existingTo)        
      );
    });

    if (overlapping) {
      return "This range overlaps with an existing range";
    }

    return null;
  };

  const handleAddLocation = async () => {
    if (!newLocationName.trim()) return;

    setIsLoading(true);
    setLoadingMessage("Creating location...");

    try {
      const existingLocation = locations.find(
        (loc) =>
          loc.location_name.toLowerCase() === newLocationName.toLowerCase()
      );

      if (existingLocation) {
        setSelectedLocation(existingLocation);
        setLoadingMessage("Location selected successfully!");
      } else {
        const locationRes = await dispatch<any>(
          createLocation({
            location_name: newLocationName,
            carrier_id: carrierId,
          })
        );
        const newLocation = unwrapResult(locationRes);
        setLocations([...locations, newLocation]);
        setSelectedLocation(newLocation);
        setLoadingMessage("Location created successfully!");
      }
      setNewLocationName("");
    } catch (err) {
      setError("Failed to create location. Please try again.");
      setSnackbarOpen(true);
      console.error(err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleAddDistanceRate = async () => {
    if (!newRangeStart.trim() || !newRangeEnd.trim() || !rate.trim() || !selectedLocation) return;

    const validationError = validateRange(newRangeStart, newRangeEnd);
    if (validationError) {
      setError(validationError);
      setSnackbarOpen(true);
      return;
    }

    setIsLoading(true);
    setLoadingMessage("Creating rate...");

    try {
      const rate_type = `${newRangeStart} to ${newRangeEnd}`;
      const rateValue = parseFloat(rate);

      const rateExists = distanceRates.some(
        (rate) =>
          rate.location?.id === selectedLocation.id &&
          rate.rate_type === rate_type
      );

      if (rateExists) {
        setError("A rate with this range already exists for this location");
        setSnackbarOpen(true);
        setIsLoading(false);
        return;
      }

      const longDistanceData = {
        rate_type,
        rate: rateValue,
        carrier_id: carrierId,
        location_id: selectedLocation.id,
      };

      const rateRes = await dispatch<any>(createLongDistance(longDistanceData));
      const newRate = unwrapResult(rateRes);
      setDistanceRates([
        ...distanceRates,
        { ...newRate, location: selectedLocation },
      ]);
      setNewRangeStart("");
      setNewRangeEnd("");
      setRate("");
      setLoadingMessage("Rate added successfully!");
    } catch (err: any) {
      if (err.payload && err.payload.includes("already exists")) {
        setError("A rate with this range already exists for this location");
        setSnackbarOpen(true);
      } else {
        setError("Failed to add rate. Please try again.");
        setSnackbarOpen(true);
      }
      console.error(err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleEditClick = (rate: DistanceRate) => {
    const [start, end] = rate.rate_type.split(" to ");
    setEditingId(rate.id!);
    setEditedRangeStart(start);
    setEditedRangeEnd(end);
    setEditedRate(rate.rate.toString());
  };

  const handleSaveChanges = async () => {
    if (editingId === null || !selectedLocation) return;
    const validationError = validateRange(editedRangeStart, editedRangeEnd, editingId);
    if (validationError) {
      setError(validationError);
      setSnackbarOpen(true);
      return;
    }
    setIsLoading(true);
    setLoadingMessage("Saving changes...");

    try {
      const rate_type = `${editedRangeStart} to ${editedRangeEnd}`;
      const rateValue = parseFloat(editedRate);

      const updateData = {
        id: editingId,
        rate_type,
        rate: rateValue,
        location_id: selectedLocation.id,
      };

      const rateRes = await dispatch<any>(updateLongDistance(updateData));
      const updatedRate = unwrapResult(rateRes);
      setDistanceRates(
        distanceRates.map((item) =>
          item.id === editingId ? updatedRate : item
        )
      );
      setEditingId(null);
      setLoadingMessage("Changes saved successfully!");
    } catch (err: any) {
      setLoadingMessage("Failed to save changes.");
      console.error(err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleDeleteDistanceRate = async (id: number) => {
    if (!id) return;

    setIsLoading(true);
    setLoadingMessage("Deleting distance rate...");

    try {
      await dispatch<any>(deleteLongDistance(id));
      setDistanceRates((prevRates) =>
        prevRates.filter((rate) => rate.id !== id)
      );
      setLoadingMessage("Distance rate deleted successfully!");
    } catch (err: any) {
      setLoadingMessage("Failed to delete distance rate.");
      console.error("Error deleting distance rate:", err);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleBackToLocations = () => {
    setSelectedLocation(null);
    setEditingId(null);
  };
  useEffect(() => {
    console.log(newLocationName, "LLL");
  }, [newLocationName]);
  const handleNumericInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, 
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };
  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <Box sx={{ width: isMobile ? "80%" : "50%", textAlign: "center" }}>
            <LinearProgress />
            <Typography fontSize={isMobile ? 14 : 16} fontWeight={500} mt={1}>
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" mb={4}>
        Long Distance Rates
      </Typography>

      {!selectedLocation ? (
        <Box>
          <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant={isMobile ? "subtitle1" : "h6"} mb={3} color="primary">
                Add New Location
              </Typography>
              <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "center", gap: 2 }}>
                <Select
                  sx={{ width: isMobile ? "100%" : "50%" }}
                  value={newLocationName || ""}
                  onChange={(e) => setNewLocationName(e.target.value)}
                  label="State"
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOn color="primary" />
                    </InputAdornment>
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select a State
                  </MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.name} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleAddLocation}
                  disabled={!newLocationName.trim()}
                  sx={{ height: "56px", width: isMobile ? "100%" : "auto" }}
                >
                  Add Location
                </Button>
              </Box>
            </CardContent>
          </Card>

          {locations.length > 0 && (
            <Card elevation={3}>
              <CardContent>
                <Typography variant={isMobile ? "subtitle1" : "h6"} mb={3} color="primary">
                  Select Existing Location
                </Typography>
                <Grid container spacing={2}>
                  {locations.map((location) => (
                    <Grid item xs={12} sm={6} md={4} key={location.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 3,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            transform: "scale(1.03)",
                            boxShadow: theme.shadows[4],
                          },
                        }}
                        onClick={() => setSelectedLocation(location)}
                      >
                        <Avatar
                          sx={{ bgcolor: theme.palette.primary.main, mb: 2 }}
                        >
                          <LocationOn />
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight={500}>
                          {location.location_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click to add rates
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 4, flexDirection: isMobile ? "column" : "row", gap: isMobile ? 2 : 0 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={handleBackToLocations}
              sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 2 : 0 }}
              fullWidth={isMobile}
            >
              Back to Locations
            </Button>
            <Typography variant={isMobile ? "h6" : "h5"} color="primary">
              {selectedLocation.location_name}
            </Typography>
          </Box>

          <Card elevation={3} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant={isMobile ? "subtitle1" : "h6"} mb={3}>
                {editingId ? "Edit Rate" : "Add New Rate"}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="From (miles)"
                    variant="outlined"
                    value={
                      editingId !== null ? editedRangeStart : newRangeStart
                    }
                    onChange={(e) =>
                      handleNumericInputChange(
                        e,
                        editingId !== null ? setEditedRangeStart : setNewRangeStart
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Directions color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="To (miles)"
                    variant="outlined"
                    value={editingId !== null ? editedRangeEnd : newRangeEnd}
                    onChange={(e) =>
                      handleNumericInputChange(
                        e,
                        editingId !== null ? setEditedRangeEnd : setNewRangeEnd
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Directions color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Rate ($)"
                    variant="outlined"
                    value={editingId !== null ? editedRate : rate}
                    onChange={(e) =>
                      handleNumericInputChange(
                        e,
                        editingId !== null ? setEditedRate : setRate
                      )
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MonetizationOn color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {editingId !== null ? (
                    <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        sx={{ flex: 1 }}
                        fullWidth={isMobile}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveChanges}
                        disabled={!editedRangeStart.trim() || !editedRangeEnd.trim() || !editedRate.trim()}
                        sx={{ flex: 1 }}
                        fullWidth={isMobile}
                      >
                        Save
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<Add />}
                      onClick={handleAddDistanceRate}
                      disabled={!newRangeStart.trim() || !newRangeEnd.trim() || !rate.trim()}
                      fullWidth
                    >
                      Add Rate
                    </Button>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {distanceRates.filter(
            (rate) => rate.location?.id === selectedLocation.id
          ).length > 0 && (
            <Card elevation={3}>
              <CardContent>
                <Typography variant={isMobile ? "subtitle1" : "h6"} mb={3}>
                  Existing Rates for {selectedLocation.location_name}
                </Typography>
                <List>
                  {distanceRates
                    .filter((rate) => rate.location?.id === selectedLocation.id)
                    .map((item) => (
                      <React.Fragment key={item.id}>
                        <ListItem
                          secondaryAction={
                            editingId === item.id ? null : (
                              <Box>
                                <IconButton
                                  edge="end"
                                  aria-label="edit"
                                  onClick={() => handleEditClick(item)}
                                  sx={{ mr: 1 }}
                                >
                                  <Edit color="primary" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() =>
                                    handleDeleteDistanceRate(item.id!)
                                  }
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            )
                          }
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <Directions color="action" sx={{ mr: 1 }} />
                                <Typography component="span" fontWeight={500}>
                                  {item.rate_type} miles
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mt: 0.5,
                                }}
                              >
                                <MonetizationOn
                                  color="action"
                                  sx={{ mr: 1, fontSize: "1rem" }}
                                />
                                <Typography component="span" variant="body2">
                                  ${item.rate}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LongDistance;